import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const SITE_URL = "https://englishforleaders.com";

const upsertMeta = (attr, key, content) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

export const usePageMeta = (title, description, path = "/", { noindex = false } = {}) => {
  useEffect(() => {
    document.title = title;
    const url = `${SITE_URL}${path}`;
    if (description) upsertMeta("name", "description", description);
    upsertMeta(
      "name",
      "robots",
      noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1"
    );
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);
    upsertMeta("property", "og:title", title);
    if (description) upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("name", "twitter:title", title);
    if (description) upsertMeta("name", "twitter:description", description);
  }, [title, description, path, noindex]);
};

export const Reveal = ({ children, delay = 0, y = 28, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const Eyebrow = ({ children, light = false }) => (
  <p
    className={`text-xs sm:text-sm font-bold uppercase tracking-[0.28em] ${
      light ? "text-white/70" : "text-navy/60"
    }`}
  >
    {children}
  </p>
);

export const PrimaryLink = ({ to, children, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group inline-flex items-center gap-2.5 rounded-sm bg-crimson px-7 py-3.5 text-sm font-bold tracking-wide text-white transition-colors duration-300 hover:bg-crimson-dark"
  >
    {children}
    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
  </Link>
);

export const SecondaryLink = ({ to, children, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="inline-flex items-center gap-2.5 rounded-sm border-2 border-navy px-7 py-3 text-sm font-bold tracking-wide text-navy transition-colors duration-300 hover:bg-navy hover:text-white"
  >
    {children}
  </Link>
);

export const ArrowLink = ({ to, children, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group inline-flex items-center gap-2.5 text-sm font-bold text-crimson"
  >
    <span className="border-b border-crimson/40 pb-0.5 transition-colors duration-300 group-hover:border-crimson">
      {children}
    </span>
    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
  </Link>
);

export const PageHero = ({ eyebrow, title, intro, testid }) => (
  <section data-testid={testid} className="border-b border-navy/10 bg-offwhite">
    <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20 lg:px-10 lg:py-24">
      <Reveal>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="mt-5 max-w-3xl font-display text-4xl tracking-tight text-navy sm:text-5xl">
          {title}
        </h1>
        {intro && (
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-charcoal/75 sm:text-lg">
            {intro}
          </p>
        )}
      </Reveal>
    </div>
  </section>
);

export const FinalCTA = () => (
  <section data-testid="final-cta" className="bg-charcoal">
    <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
      <Reveal className="max-w-3xl">
        <h2 className="font-display text-3xl leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
          Better Communication. Stronger Leadership.{" "}
          <span className="text-crimson">Greater Possibilities.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/75 sm:text-lg">
          Whether you're strengthening your school, building international
          partnerships or implementing educational change, let's discuss how
          practical experience and clear communication can help.
        </p>
        <div className="mt-10">
          <PrimaryLink to="/contact" testid="final-cta-button">
            Start a Conversation
          </PrimaryLink>
        </div>
      </Reveal>
    </div>
  </section>
);
